import type { AppStatus } from "@app/lib/api/status";
import { useAuth } from "@app/lib/auth/AuthContext";
import { getActiveLocale } from "@app/lib/i18n/active_locale";
import { formatList } from "@app/lib/i18n/format";
import {
  isDustCompanyPlan,
  isEnterprisePlanPrefix,
} from "@app/lib/plans/plan_codes";
import { useProgrammaticUsageLimit } from "@app/lib/swr/usage_settings";
import { useAppStatus } from "@app/lib/swr/useAppStatus";
import { useWorkspaceUsageStatus } from "@app/lib/swr/user";
import { useMetronomeContract } from "@app/lib/swr/workspaces";
import { DEFAULT_EMBEDDING_PROVIDER_ID } from "@app/types/assistant/models/embedding";
import type { ByokModelProviderIdType } from "@app/types/assistant/models/types";
import { PRETTIFIED_PROVIDER_NAMES } from "@app/types/provider_selection";
import type { LightWorkspaceType, WorkspaceType } from "@app/types/user";
import { isAdmin } from "@app/types/user";
import { cn, LinkWrapper } from "@dust-tt/sparkle";
import { Trans, useLingui } from "@lingui/react/macro";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";

const statusBannerVariants = cva(
  "flex flex-col gap-0.5 border-b px-4 py-2 text-sm",
  {
    variants: {
      variant: {
        info: cn("border-info-100", "bg-info-50", "text-info-800"),
        orange: cn(
          "border-orange-100",
          "bg-orange-50",
          "text-orange-800",
          "dark:border-info-100 dark:bg-info-50 dark:text-info-700"
        ),
        warning: cn("border-warning-100", "bg-warning-50", "text-warning-800"),
        success: cn("border-success-200", "bg-success-50", "text-success-800"),
        danger: cn("border-red-100", "bg-red-50", "text-red-800"),
      },
    },
    defaultVariants: {
      variant: "info",
    },
  }
);

interface StatusBannerProps extends VariantProps<typeof statusBannerVariants> {
  title: string;
  description: React.ReactNode;
  footer?: React.ReactNode;
}

function StatusBanner({
  variant,
  title,
  description,
  footer,
}: StatusBannerProps) {
  return (
    <div className={statusBannerVariants({ variant })}>
      <div className="font-semibold">{title}</div>
      <div className="font-normal">
        {description}
        {footer && <> {footer}</>}
      </div>
    </div>
  );
}

interface AppStatusBannerProps {
  appStatus: AppStatus;
}

function AppStatusBanner({ appStatus }: AppStatusBannerProps) {
  const { providersStatus, dustStatus } = appStatus;

  if (dustStatus) {
    return (
      <StatusBanner
        variant="orange"
        title={dustStatus.name}
        description={dustStatus.description}
        footer={
          <>
            <Trans>
              Check our{" "}
              <LinkWrapper
                href={dustStatus.link}
                target="_blank"
                className="underline"
              >
                status page
              </LinkWrapper>{" "}
              for updates.
            </Trans>
          </>
        }
      />
    );
  }

  if (providersStatus) {
    return (
      <StatusBanner
        variant="orange"
        title={providersStatus.name}
        description={providersStatus.description}
      />
    );
  }

  return null;
}

interface UnhealthyCredentialsBannerProps {
  owner: WorkspaceType;
}

function UnhealthyCredentialsBanner({
  owner,
}: UnhealthyCredentialsBannerProps) {
  const { t } = useLingui();
  const { providersHealth } = useAuth();

  if (!providersHealth) {
    return null;
  }

  const title = t`Your workspace is not operational`;
  const footer = isAdmin(owner) ? (
    <LinkWrapper href={`/w/${owner.sId}/model-providers`} className="underline">
      <Trans>Model providers</Trans>
    </LinkWrapper>
  ) : (
    <Trans>Contact your workspace admin.</Trans>
  );

  const hasConfiguredProviders = Object.keys(providersHealth).length > 0;
  const hasConfiguredEmbeddingProvider =
    providersHealth[DEFAULT_EMBEDDING_PROVIDER_ID] !== undefined;
  const isWorkspaceHealthy = Object.values(providersHealth).every(Boolean);

  let description: string | null = null;
  if (!hasConfiguredProviders) {
    description = t`No provider credentials configured. Please set up at least one model provider.`;
  } else if (!hasConfiguredEmbeddingProvider) {
    description = t`Please set up your OpenAI credentials.`;
  } else if (!isWorkspaceHealthy) {
    const invalidProviderNames = formatList(
      Object.entries(providersHealth)
        .filter(([_, isHealthy]) => !isHealthy)
        .map(
          ([providerId]) =>
            PRETTIFIED_PROVIDER_NAMES[providerId as ByokModelProviderIdType]
        ),
      { type: "conjunction" },
      getActiveLocale()
    );
    description = t`The following model providers have invalid credentials: ${invalidProviderNames}.`;
  } else {
    return null;
  }

  return (
    <StatusBanner
      title={title}
      description={description}
      variant="warning"
      footer={footer}
    />
  );
}

function SubscriptionPastDueBanner() {
  const { t } = useLingui();
  return (
    <StatusBanner
      variant="warning"
      title={t`Your payment has failed!`}
      description={
        <>
          <br />
          <Trans>
            Please make sure to update your payment method in the Admin section
            to maintain access to your workspace. We will retry in a few days.
          </Trans>
          <br />
          <br />
          <Trans>
            After 3 attempts, your workspace will be downgraded to the free
            plan. Connections will be deleted and members will be revoked.
            Details{" "}
            <LinkWrapper
              href="https://getazimut.com"
              target="_blank"
              className="underline"
            >
              here
            </LinkWrapper>
            .
          </Trans>
        </>
      }
    />
  );
}

interface WorkspaceUsageStatusBannerProps {
  owner: LightWorkspaceType;
}

function WorkspaceUsageStatusBanner({
  owner,
}: WorkspaceUsageStatusBannerProps) {
  const { t } = useLingui();
  const {
    poolCreditState,
    programmaticCreditStatus,
    programmaticWarningReached,
    balanceThresholdReached,
  } = useWorkspaceUsageStatus({ owner });

  // The low/critical pool states are internal throttling signals and are not
  // surfaced. Admins are alerted when the pool is fully depleted (agents
  // blocked) or when it has run into pay-as-you-go overage.
  const isPoolDepleted = poolCreditState === "depleted";
  const isPoolOverage = poolCreditState === "overage";

  // When the contract sells seats that carry personal credits (pro/max/free),
  // those users spend their own credits before the shared pool and keep working
  // even when the pool is depleted/in overage (mirrors the personal-seat
  // carve-out in `isUserBlocked`). The pool banner's "out of credits / agents
  // blocked" message would be misleading there, so we suppress it. Only fetch
  // the contract (a Metronome API call) when the banner would otherwise show.
  const poolStateWouldBanner =
    isAdmin(owner) && (isPoolDepleted || isPoolOverage);
  const { contract, isMetronomeContractLoading } = useMetronomeContract({
    workspaceId: owner.sId,
    disabled: !poolStateWouldBanner,
  });
  const showPoolBanner =
    poolStateWouldBanner &&
    !isMetronomeContractLoading &&
    !contract?.hasPersonalCreditSeats;
  // A cap of 0 or null is a deliberate hard block on programmatic API, not an
  // exhausted budget, so its "cap reached" banner is suppressed. The cap is
  // only fetched when the banner would otherwise show.
  const programmaticStateWouldBanner =
    isAdmin(owner) &&
    (programmaticCreditStatus === "depleted" || programmaticWarningReached);
  const { programmaticUsageLimit, isProgrammaticUsageLimitLoading } =
    useProgrammaticUsageLimit({
      workspaceId: owner.sId,
      disabled: !programmaticStateWouldBanner,
    });
  const monthlyCapCredits = programmaticUsageLimit?.monthlyCapCredits ?? 0;
  const showProgrammaticBanner =
    programmaticStateWouldBanner &&
    !isProgrammaticUsageLimitLoading &&
    monthlyCapCredits !== 0;
  // The admin-configured balance-threshold warning is only relevant before the
  // pool is depleted/overage — those states have their own (stronger) banner.
  const showBalanceThresholdBanner =
    isAdmin(owner) &&
    balanceThresholdReached &&
    !isPoolDepleted &&
    !isPoolOverage;

  // Only a single usage banner is shown at a time, picked by priority. The
  // pool banner takes precedence over the lower-severity balance-threshold
  // warning, which is why "Your credit balance is running low" never shows
  // alongside it.
  const manageCreditsFooter = (
    <LinkWrapper href={`/w/${owner.sId}/credits`} className="underline">
      <Trans>Manage credits</Trans>
    </LinkWrapper>
  );

  const banner = ((): StatusBannerProps | null => {
    if (showPoolBanner) {
      return {
        variant: isPoolDepleted ? "danger" : "warning",
        title: isPoolDepleted
          ? t`Your workspace is out of credits`
          : t`Your workspace has used all its credits`,
        description: isPoolDepleted
          ? t`Your workspace has run out of credits. Agents are blocked until you top up.`
          : t`Your workspace has used all of its included credits and is now billed pay-as-you-go. Top up to avoid overage charges.`,
        footer: manageCreditsFooter,
      };
    }

    if (showProgrammaticBanner) {
      return {
        variant: programmaticCreditStatus === "depleted" ? "danger" : "warning",
        title:
          programmaticCreditStatus === "depleted"
            ? t`Programmatic API cap reached`
            : t`Programmatic API cap at 80%`,
        description:
          programmaticCreditStatus === "depleted"
            ? t`Your workspace has exhausted its monthly programmatic API credit cap. Programmatic API calls are blocked until the billing cycle resets or the cap is raised.`
            : t`Your workspace has used 80% of its monthly programmatic API credit cap. Consider raising the cap to avoid interruptions.`,
        footer: (
          <LinkWrapper href={`/w/${owner.sId}/credits`} className="underline">
            <Trans>Manage usage</Trans>
          </LinkWrapper>
        ),
      };
    }

    if (showBalanceThresholdBanner) {
      return {
        variant: "warning",
        title: t`Your credit balance is running low`,
        description: t`Your workspace's remaining credit balance has dropped below the threshold you set. Top up to avoid running out of credits.`,
        footer: manageCreditsFooter,
      };
    }

    return null;
  })();

  if (!banner) {
    return null;
  }

  return <StatusBanner {...banner} />;
}

export function StatusBanners() {
  const { workspace: owner, subscription } = useAuth();
  const { appStatus } = useAppStatus();

  return (
    <>
      <WorkspaceUsageStatusBanner owner={owner} />
      <UnhealthyCredentialsBanner owner={owner} />
      {appStatus && <AppStatusBanner appStatus={appStatus} />}
      {subscription.paymentFailingSince &&
        isAdmin(owner) &&
        !isDustCompanyPlan(subscription.plan.code) &&
        !isEnterprisePlanPrefix(subscription.plan.code) && (
          <SubscriptionPastDueBanner />
        )}
    </>
  );
}
